import { type FC, type ReactNode } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { SpinnerLoader, Typography } from 'shared/ui'
import { type ITableProps, Table } from 'shared/ui/TableNew'

import { type AircallNumbersListStore } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/store/AircallNumbersListStore'
import { RefreshNumbersButton } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/ui/RefreshNumbersButton/RefreshNumbersButton'
import { type AircallNumberRow } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationAircallSettings/model/AircallNumberRow/AircallNumberRow'
import styles from './styles.module.scss'

type IAircallTableProps = {
  store: AircallNumbersListStore
  notFoundMessage?: ReactNode
  emptyContent?: ReactNode
  rows: AircallNumberRow[]
} & Omit<ITableProps<AircallNumberRow>, 'store'>

export const AircallTable: FC<IAircallTableProps> = observer(
  ({ store, notFoundMessage, emptyContent, ...tableProps }) => {
    const { loading, firstLoading, tableStore } = store

    const getContent = () => {
      if (firstLoading && !tableProps.rows.length) {
        return <SpinnerLoader ariaLabel={'AircallTable'} />
      }

      if (!tableProps.rows.length && emptyContent && !loading) {
        return emptyContent
      }

      if (!tableStore.loading && !tableProps.rows.length) {
        return (
          <div className={styles.emptySearch}>
            <Typography
              variant={'body-rg-medium'}
              ellipsis
              ariaLabel={'empty_placeholder'}
              color={'tertiary'}
            >
              {notFoundMessage || 'No Aircall numbers found'}
            </Typography>
          </div>
        )
      }

      return (
        <div className={classNames(styles.table, { [styles.loading]: loading })}>
          <Table
            store={tableStore}
            rightHeaderContent={<RefreshNumbersButton store={store} />}
            {...tableProps}
          />
        </div>
      )
    }

    return <div className={styles.root}>{getContent()}</div>
  }
)
